// utils/pdfHelper.js
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/** Wait until all <img> are settled so html2canvas captures them */
async function waitForImages(container) {
  const imgs = Array.from(container.querySelectorAll("img"));
  if (!imgs.length) return;
  await Promise.all(
    imgs.map(
      (img) =>
        img.complete ||
        new Promise((res) => {
          img.onload = img.onerror = res;
        })
    )
  );
}

/** Height including top/bottom margins */
function getOuterHeightWithMargins(el) {
  const cs = window.getComputedStyle(el);
  const mt = parseFloat(cs.marginTop) || 0;
  const mb = parseFloat(cs.marginBottom) || 0;
  return el.offsetHeight + mt + mb;
}

/** Measure header height of a wrapper */
function measureWrapperHeaderHeight(wrapper) {
  const header = wrapper.querySelector("[data-pdf-header]");
  if (header) return getOuterHeightWithMargins(header);

  // Fallback: measure any non-subblock content
  const clone = wrapper.cloneNode(true);
  clone.querySelectorAll("[data-pdf-subblock]").forEach((sb) => sb.remove());

  const tempDiv = document.createElement("div");
  tempDiv.style.position = "absolute";
  tempDiv.style.left = "-9999px";
  tempDiv.style.width = "794px";
  tempDiv.appendChild(clone);
  document.body.appendChild(tempDiv);

  const h = getOuterHeightWithMargins(clone);
  document.body.removeChild(tempDiv);
  return h;
}

/** Clone wrapper with subset of subblocks */
function cloneWrapperWithSubset(wrapper, subblocks) {
  const clone = wrapper.cloneNode(true);

  // Remove all existing subblocks
  clone.querySelectorAll("[data-pdf-subblock]").forEach((sb) => sb.remove());

  // Find the container (element with p-4, p-5, p-6 class or the clone itself)
  let container = clone.querySelector(".p-4, .p-5, .p-6") || clone;

  // If there's a header, the container should be after it
  const header = clone.querySelector("[data-pdf-header]");
  if (header && header.nextElementSibling) {
    container = header.nextElementSibling;
  }

  // Add the subset of subblocks
  subblocks.forEach((sb) => {
    container.appendChild(sb.cloneNode(true));
  });

  return clone;
}

/**
 * Export preview to a multi-page A4 PDF
 */
export async function exportPreviewToPDFByPages(
  previewEl,
  filename = "report.pdf"
) {
  if (!previewEl) return;

  // Store original styles
  const original = {
    height: previewEl.style.height,
    maxHeight: previewEl.style.maxHeight,
    overflowY: previewEl.style.overflowY,
    overflowX: previewEl.style.overflowX,
  };

  // Expand to measure full content
  previewEl.style.height = "auto";
  previewEl.style.maxHeight = "none";
  previewEl.style.overflowY = "visible";
  previewEl.style.overflowX = "visible";

  await waitForImages(previewEl);

  // Get the actual content
  const contentEl =
    previewEl.querySelector('[style*="padding"]') ||
    previewEl.firstElementChild ||
    previewEl;

  // Page setup
  const A4_W_MM = 210;
  const A4_H_MM = 297;
  const pageWidthPx = 794;
  const pageHeightPx = Math.floor(pageWidthPx * (A4_H_MM / A4_W_MM));
  const pagePadding = 40;
  const usableHeight = pageHeightPx - pagePadding * 2;

  // Collect all blocks
  const blocks = Array.from(contentEl.querySelectorAll("[data-pdf-block]"));

  if (blocks.length === 0) {
    console.warn("No blocks found with data-pdf-block");
    blocks.push(contentEl);
  }

  // Build pages
  const pages = [];
  let currentPage = [];
  let currentPageHeight = 0;

  const addNewPage = () => {
    if (currentPage.length > 0) {
      pages.push(currentPage);
      currentPage = [];
      currentPageHeight = 0;
    }
  };

  for (const block of blocks) {
    const forceBreak = block.getAttribute("data-pdf-break-before") === "always";

    if (forceBreak && currentPage.length > 0) {
      addNewPage();
    }

    const subblocks = Array.from(block.querySelectorAll("[data-pdf-subblock]"));

    if (subblocks.length === 0) {
      // No subblocks - treat as atomic
      const blockHeight = getOuterHeightWithMargins(block);

      if (
        currentPageHeight > 0 &&
        currentPageHeight + blockHeight > usableHeight
      ) {
        addNewPage();
      }

      currentPage.push(block.cloneNode(true));
      currentPageHeight += blockHeight;
    } else {
      // Has subblocks - handle pagination
      const headerHeight = measureWrapperHeaderHeight(block);
      let pendingSubblocks = [];
      let pendingHeight = headerHeight;

      for (const subblock of subblocks) {
        const subblockHeight = getOuterHeightWithMargins(subblock);

        // Check if we need a new page
        if (
          pendingSubblocks.length > 0 &&
          currentPageHeight + pendingHeight + subblockHeight > usableHeight
        ) {
          // Create wrapper with current pending subblocks
          const wrapperClone = cloneWrapperWithSubset(block, pendingSubblocks);
          currentPage.push(wrapperClone);

          // Start new page
          addNewPage();
          pendingSubblocks = [];
          pendingHeight = headerHeight;
        }

        // Check if starting fresh on a page and still won't fit
        if (
          pendingSubblocks.length === 0 &&
          currentPageHeight + headerHeight + subblockHeight > usableHeight &&
          currentPageHeight > 0
        ) {
          addNewPage();
        }

        pendingSubblocks.push(subblock);
        pendingHeight += subblockHeight;
      }

      // Add remaining subblocks
      if (pendingSubblocks.length > 0) {
        if (
          currentPageHeight > 0 &&
          currentPageHeight + pendingHeight > usableHeight
        ) {
          addNewPage();
        }

        const wrapperClone = cloneWrapperWithSubset(block, pendingSubblocks);
        currentPage.push(wrapperClone);
        currentPageHeight += pendingHeight;
      }
    }
  }

  // Add final page
  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  // Create PDF
  const pdf = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  // Render each page
  for (let pageIdx = 0; pageIdx < pages.length; pageIdx++) {
    if (pageIdx > 0) {
      pdf.addPage();
    }

    // Create page container
    const pageContainer = document.createElement("div");
    pageContainer.style.position = "absolute";
    pageContainer.style.left = "-9999px";
    pageContainer.style.top = "0";
    pageContainer.style.width = `${pageWidthPx}px`;
    pageContainer.style.minHeight = `${pageHeightPx}px`;
    pageContainer.style.backgroundColor = "#ffffff";
    pageContainer.style.padding = `${pagePadding}px`;
    pageContainer.style.boxSizing = "border-box";
    pageContainer.style.fontFamily = "system-ui, -apple-system, sans-serif";
    pageContainer.style.fontSize = "14px";
    pageContainer.style.lineHeight = "1.5";
    pageContainer.style.color = "#000000";

    // Copy classes from original for Tailwind
    pageContainer.className = contentEl.className || "";

    // Add page elements
    pages[pageIdx].forEach((element) => {
      pageContainer.appendChild(element);
    });

    document.body.appendChild(pageContainer);

    // Wait for images and rendering
    await waitForImages(pageContainer);
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // Capture the page
      const canvas = await html2canvas(pageContainer, {
        width: pageWidthPx,
        height: pageHeightPx,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        onclone: (clonedDoc, element) => {
          const clonedContainer = clonedDoc.querySelector('[style*="-9999px"]');
          if (clonedContainer) {
            clonedContainer.style.left = "0";
            clonedContainer.style.position = "relative";
          }

          // Fix all grid items in the findings table
          clonedDoc
            .querySelectorAll(".grid.grid-cols-6 > div")
            .forEach((cell) => {
              // Add padding-top to simulate vertical centering
              const currentPadding = cell.style.padding || "";
              if (!currentPadding.includes("px")) {
                cell.style.paddingTop = "6px";
                cell.style.paddingBottom = "6px";
              }

              // Force line-height for better vertical alignment
              cell.style.lineHeight = "1.5";

              // If it has flex classes, ensure they're applied
              if (
                cell.className.includes("flex") ||
                cell.className.includes("items-center")
              ) {
                cell.style.display = "flex";
                cell.style.alignItems = "center";
                cell.style.minHeight = "28px";
              }
            });

          // Specifically target the header cells with dark background
          clonedDoc
            .querySelectorAll(".bg-\\[\\#1D4A72\\] > div")
            .forEach((headerCell) => {
              headerCell.style.display = "flex";
              headerCell.style.alignItems = "center";
              headerCell.style.justifyContent = "center";
              headerCell.style.minHeight = "32px";
              headerCell.style.padding = "8px 4px";
            });

          // Target value cells that should be left-aligned but vertically centered
          clonedDoc
            .querySelectorAll(".grid.grid-cols-6.gap-x-4 > div")
            .forEach((valueCell) => {
              valueCell.style.display = "flex";
              valueCell.style.alignItems = "center";
              valueCell.style.minHeight = "28px";
              valueCell.style.paddingTop = "6px";
              valueCell.style.paddingBottom = "6px";
            });
        },
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.95);
      pdf.addImage(imgData, "JPEG", 0, 0, A4_W_MM, A4_H_MM);
    } catch (error) {
      console.error("Error capturing page", pageIdx, error);
    }

    document.body.removeChild(pageContainer);
  }

  // Restore original styles
  previewEl.style.height = original.height;
  previewEl.style.maxHeight = original.maxHeight;
  previewEl.style.overflowY = original.overflowY;
  previewEl.style.overflowX = original.overflowX;

  pdf.save(filename);
}

// Alias for backward compatibility
export async function exportPreviewToPDF(previewEl, filename = "report.pdf") {
  return exportPreviewToPDFByPages(previewEl, filename);
}
